import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, CheckCircle, Phone } from "lucide-react";
import { Helmet } from "react-helmet-async";

import SEO from "../components/SEO";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { getGeoPages, getCompanyInfo } from "../services/api";

const ServiceAreasPage = () => {
  const [geoPages, setGeoPages] = useState([]);
  const [companyInfo, setCompanyInfo] = useState({
    phone: "1-888-907-3777"
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [geoPagesData, companyInfoData] = await Promise.all([
          getGeoPages(),
          getCompanyInfo()
        ]);

        setGeoPages(geoPagesData);
        setCompanyInfo(companyInfoData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div>
      <SEO
        title="Septic Services in Thunder Bay & Surrounding Areas | Groundhogs Septics"
        description="Professional septic system installation, septic field replacement, Eljen GSF systems, and rural septic services in Thunder Bay, Oliver Paipoonge, Shuniah, Kakabeka, Gorham, Nipigon, Greenstone, Dryden, Neebing, and surrounding lakefront cottage areas."
        keywords="septic services Thunder Bay, septic installers near me, septic installation Thunder Bay, septic field replacement Thunder Bay, rural septic installs Northwestern Ontario"
        canonicalPath="/service-areas"
      />
      <Header />

      {/* Hero + Local Conditions */}
      <section className="gh-intro pt-32 pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="gh-intro-heading text-center mb-10" data-reveal>
            <h1
              className="text-5xl font-bold mb-4"
              style={{ color: "var(--dark-navy)" }}
            >
              Septic Services in Thunder Bay &amp; Surrounding Areas
            </h1>
            <p
              className="text-xl mb-4"
              style={{ color: "var(--primary-orange)" }}
            >
              Engineered septic system installation, field replacement, and Eljen GSF systems across
              Northwestern Ontario.
            </p>
            <p
              className="max-w-3xl mx-auto text-lg mb-4"
              style={{ color: "var(--text-light)" }}
            >
              We provide complete septic services for Thunder Bay and nearby communities including Oliver
              Paipoonge, Shuniah, Kakabeka, Gorham, Nipigon, Greenstone, Dryden, Neebing, lakefront cottages,
              and rural septic installs in unorganized townships.
            </p>
            <p
              className="max-w-3xl mx-auto text-base mb-4"
              style={{ color: "var(--text-light)" }}
            >
              Serving homeowners, cottages, rural properties, and unorganized townships across Thunder Bay for
              septic installation, repair, and full system replacement.
            </p>

            <div className="max-w-3xl mx-auto mt-2 text-left md:text-center">
              <h2
                className="text-2xl font-bold mb-2"
                style={{ color: "var(--dark-navy)" }}
              >
                The Most Trusted Septic Installers in Thunder Bay &amp; Northern Ontario
              </h2>
              <p className="text-base" style={{ color: "var(--text-light)" }}>
                Groundhogs&trade; has completed over 1,200 septic installations across Northwestern Ontario,
                making us the most experienced septic system installer in the Thunder Bay region.
              </p>
            </div>
          </div>

          {/* SECTION A — Thunder Bay Soil & Environmental Conditions */}
          <div className="max-w-4xl mx-auto mb-16">
            <h2
              className="text-2xl font-bold mb-3 text-center md:text-left"
              style={{ color: "var(--dark-navy)" }}
            >
              Why Septic Systems in Thunder Bay Require Specialized Engineering
            </h2>
            <p className="mb-3 text-base" style={{ color: "var(--text-light)" }}>
              Thunder Bay and Northwestern Ontario have some of the most challenging septic installation
              conditions in the province. Heavy clay soils, shallow granite bedrock, glacial till, and
              fluctuating groundwater levels all affect how wastewater moves underground. Many rural
              areas—such as Shuniah, Pass Lake, Kaministiquia, Neebing, Nolalu, and Slate River—have poor
              natural drainage, requiring engineered designs, imported sand, raised beds, or advanced
              systems like Eljen GSF.
            </p>
            <p className="text-base" style={{ color: "var(--text-light)" }}>
              These soil conditions also change dramatically from one property to the next. A home on
              Oliver Road may have sandy soil suited for a traditional system, while a property five
              minutes away in Gorham may require a full Eljen mound due to high water table. Our team
              evaluates soil structure, drainage patterns, groundwater height, frost depth, and property
              slope to design systems built specifically for Northern Ontario&apos;s environment.
            </p>
          </div>

          {/* Service Area Map */}
          <div className="mb-16 rounded-lg overflow-hidden shadow-lg">
            <img
              src="/service-area-map.png"
              alt="Northwestern Ontario Service Area Map - Thunder Bay and surrounding communities"
              className="w-full h-auto"
            />
          </div>

          {/* Main Service Areas Card */}
          <div className="mb-16">
            <h2
              className="text-3xl font-bold mb-8 text-center"
              style={{ color: "var(--dark-navy)" }}
            >
              Primary Service Locations
            </h2>
            <div
              className="bg-white p-8 rounded-lg shadow-md border-2"
              style={{ borderColor: "var(--primary-orange)" }}
            >
              <div className="flex items-center space-x-3 mb-6">
                <MapPin size={32} style={{ color: "var(--primary-orange)" }} />
                <h3
                  className="text-2xl font-bold"
                  style={{ color: "var(--dark-navy)" }}
                >
                  Thunder Bay &amp; Northwestern Ontario
                </h3>
              </div>
              <div
                className="mb-3 px-3 py-1 rounded inline-block"
                style={{ backgroundColor: "var(--primary-orange)", color: "white" }}
              >
                <span className="text-sm font-semibold">HEADQUARTERS: THUNDER BAY</span>
              </div>
              <p
                className="mb-4 text-lg"
                style={{ color: "var(--text-light)" }}
              >
                Professional septic installation services throughout Northwestern Ontario. We specialize in
                new traditional and Eljen mound system installations.
              </p>
              <ul className="space-y-2 mb-2">
                <li className="flex items-center space-x-2">
                  <CheckCircle size={18} style={{ color: "var(--primary-orange)" }} />
                  <span className="text-sm">3-Day Average Installation Timeline</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle size={18} style={{ color: "var(--primary-orange)" }} />
                  <span className="text-sm">Permit Expertise &amp; Handling</span>
                </li>
              </ul>
              <p className="text-xs" style={{ color: "var(--text-light)" }}>
                Virtual estimates and in-house financing options are available for most septic installation
                projects in Thunder Bay and surrounding areas.
              </p>
            </div>
          </div>

          {/* Territory Coverage Overview */}
          <div className="mb-16">
            <h2
              className="text-3xl font-bold mb-4 text-center"
              style={{ color: "var(--dark-navy)" }}
            >
              Where We Install Septic Systems
            </h2>
            <p
              className="max-w-3xl mx-auto text-lg mb-6 text-center"
              style={{ color: "var(--text-light)" }}
            >
              Our crews regularly complete septic installations, septic field replacements, and Eljen GSF
              systems throughout Thunder Bay and the surrounding region. If you&apos;re searching for septic
              installers near me in Northwestern Ontario, there is a good chance we already work in your
              area.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div
                className="p-6 rounded-lg border"
                style={{ borderColor: "var(--light-gray)" }}
              >
                <h3
                  className="font-bold mb-2"
                  style={{ color: "var(--dark-navy)" }}
                >
                  Thunder Bay &amp; Nearby Communities
                </h3>
                <ul
                  className="list-disc pl-5 text-sm"
                  style={{ color: "var(--text-light)" }}
                >
                  <li>City of Thunder Bay</li>
                  <li>Oliver Paipoonge</li>
                  <li>Gorham</li>
                  <li>Neebing</li>
                  <li>Kakabeka &amp; Kaministiquia corridor</li>
                </ul>
              </div>

              <div
                className="p-6 rounded-lg border"
                style={{ borderColor: "var(--light-gray)" }}
              >
                <h3
                  className="font-bold mb-2"
                  style={{ color: "var(--dark-navy)" }}
                >
                  North Shore &amp; Highway 11/17 Corridor
                </h3>
                <ul
                  className="list-disc pl-5 text-sm"
                  style={{ color: "var(--text-light)" }}
                >
                  <li>Shuniah &amp; Pass Lake</li>
                  <li>Nipigon &amp; Lake Helen</li>
                  <li>Greenstone (Geraldton, Longlac area)</li>
                  <li>Lake Superior &amp; inland lakes cottages</li>
                </ul>
              </div>

              <div
                className="p-6 rounded-lg border"
                style={{ borderColor: "var(--light-gray)" }}
              >
                <h3
                  className="font-bold mb-2"
                  style={{ color: "var(--dark-navy)" }}
                >
                  Northwestern Ontario &amp; Rural Properties
                </h3>
                <ul
                  className="list-disc pl-5 text-sm"
                  style={{ color: "var(--text-light)" }}
                >
                  <li>Dryden &amp; surrounding area</li>
                  <li>Rural septic installs in unorganized townships</li>
                  <li>Lakefront cottages &amp; camp properties</li>
                  <li>Remote road-access sites by request</li>
                </ul>
              </div>
            </div>
          </div>

          {/* SECTION B — High-Intent Keyword Block */}
          <div className="mb-16">
            <h2
              className="text-3xl font-bold mb-4 text-center"
              style={{ color: "var(--dark-navy)" }}
            >
              Septic Installers in Thunder Bay You Can Trust
            </h2>
            <p
              className="max-w-3xl mx-auto text-lg mb-4 text-center"
              style={{ color: "var(--text-light)" }}
            >
              If you&apos;re searching for <strong>septic installers near me</strong> or
              <strong> septic system installation Thunder Bay</strong>, Groundhogs&trade; is the region&apos;s most
              experienced team, with over 1,200 installations completed across Northwestern Ontario. We
              design and install systems that meet all Ontario Building Code requirements and perform
              reliably in clay, rock, and high-water-table conditions common throughout Thunder Bay.
            </p>
            <p
              className="max-w-3xl mx-auto text-lg text-center"
              style={{ color: "var(--text-light)" }}
            >
              Whether you need a new septic system, a replacement field, or an advanced Eljen GSF solution,
              our crews deliver fast, engineered, code-compliant installations designed for longevity.
            </p>
          </div>

          {/* SECTION D — Why Homeowners Choose Groundhogs in Thunder Bay */}
          <div className="mb-16">
            <h2
              className="text-3xl font-bold mb-4 text-center"
              style={{ color: "var(--dark-navy)" }}
            >
              Why Homeowners Choose Groundhogs in Thunder Bay
            </h2>
            <p
              className="max-w-3xl mx-auto text-lg mb-4 text-center"
              style={{ color: "var(--text-light)" }}
            >
              Groundhogs&trade; focuses exclusively on engineered septic systems for Northern Ontario
              conditions. We combine local soil knowledge, Eljen certification, and a systemized installation
              process to deliver reliable results on properties that many generic contractors struggle with.
            </p>
            <p
              className="max-w-3xl mx-auto text-base text-center"
              style={{ color: "var(--text-light)" }}
            >
              Instead of treating every lot the same, we evaluate your soil profile, groundwater, bedrock
              depth, and access constraints to recommend the right solution—whether that&apos;s a traditional
              field, Eljen GSF system, or raised bed. This local, engineered approach is why Thunder Bay
              homeowners, rural property owners, and lakefront cottage clients consistently choose Groundhogs
              for septic installation and replacement.
            </p>
          </div>

          {/* SECTION C — FAQ Block */}
          <div className="mb-16">
            <h2
              className="text-3xl font-bold mb-6 text-center"
              style={{ color: "var(--dark-navy)" }}
            >
              Frequently Asked Questions — Thunder Bay Septic Services
            </h2>
            <p
              className="max-w-3xl mx-auto text-base mb-4 text-center"
              style={{ color: "var(--text-light)" }}
            >
              These are the most common questions we receive from homeowners across Thunder Bay, Shuniah,
              Oliver Paipoonge, and surrounding rural communities.
            </p>
            <div
              className="max-w-3xl mx-auto space-y-5"
              style={{ color: "var(--text-light)" }}
            >
              <div>
                <h3 className="font-semibold mb-1">
                  Q: How long does a septic installation take in Thunder Bay?
                </h3>
                <p>
                  A: Most installations take about 3 days on average depending on soil, elevation, and access conditions.
                  Complex sites with clay, bedrock, or high groundwater may take slightly longer but are
                  still completed as efficiently as possible.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: Do you service rural and unorganized townships?
                </h3>
                <p>
                  A: Yes — we regularly install systems in rural townships, unorganized areas, lakefront
                  lots, and remote-access properties throughout Northwestern Ontario.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: What type of septic system is best for clay soil?
                </h3>
                <p>
                  A: Eljen GSF systems or engineered raised beds usually perform best in Thunder Bay&apos;s
                  clay-heavy regions. We assess your exact soil and groundwater conditions before making a
                  recommendation.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: Can you install septic systems near lakes or rivers?
                </h3>
                <p>
                  A: Yes — but these areas often require advanced treatment systems, specific setback
                  distances, and careful engineering to protect water quality. We work closely with local
                  authorities to ensure compliance.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">
                  Q: Do you handle permits and engineered drawings?
                </h3>
                <p>
                  A: Yes — Groundhogs manages all Ontario Building Code permits, soil evaluations, and
                  engineered designs so that your installation is fully approved and documented.
                </p>
              </div>
            </div>
          </div>

          {/* What We Do in These Areas */}
          <div className="mb-16">
            <h2
              className="text-3xl font-bold mb-4 text-center"
              style={{ color: "var(--dark-navy)" }}
            >
              Septic Services Available in All Covered Areas
            </h2>
            <p
              className="max-w-3xl mx-auto text-lg mb-6 text-center"
              style={{ color: "var(--text-light)" }}
            >
              In every community we serve, we provide complete septic installation, field replacement, and
              repair services designed for Northern Ontario&apos;s clay, bedrock, and high-water-table
              conditions.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <div
                className="p-6 rounded-lg border"
                style={{ borderColor: "var(--light-gray)" }}
              >
                <h3
                  className="font-bold mb-2"
                  style={{ color: "var(--dark-navy)" }}
                >
                  New Septic System Design &amp; Installation
                </h3>
                <p
                  className="mb-3 text-sm"
                  style={{ color: "var(--text-light)" }}
                >
                  Complete septic system design and installation using traditional leach fields or advanced
                  Eljen GSF systems for challenging sites.
                </p>
                <Link
                  to="/services/septic-system-installation-thunder-bay"
                  className="text-orange-600 text-sm font-semibold hover:underline"
                >
                  View Septic System Installation in Thunder Bay
                </Link>
              </div>

              <div
                className="p-6 rounded-lg border"
                style={{ borderColor: "var(--light-gray)" }}
              >
                <h3
                  className="font-bold mb-2"
                  style={{ color: "var(--dark-navy)" }}
                >
                  Septic Field Replacement &amp; Upgrades
                </h3>
                <p
                  className="mb-3 text-sm"
                  style={{ color: "var(--text-light)" }}
                >
                  Replacement of failed or aging fields with engineered traditional or Eljen systems sized
                  for your soil, slope, and groundwater conditions.
                </p>
                <Link
                  to="/services/septic-field-installation-replacement-thunder-bay"
                  className="text-orange-600 text-sm font-semibold hover:underline"
                >
                  Learn About Septic Field Installation &amp; Replacement
                </Link>
                <p className="mt-2 text-xs" style={{ color: "var(--text-light)" }}>
                  If your existing system is failing or saturated, our
                  <Link
                    to="/services/septic-system-replacement-thunder-bay"
                    className="text-orange-600 font-semibold ml-1 hover:underline"
                  >
                    septic system replacement team in Thunder Bay
                  </Link>
                  can design and install a full replacement field.
                </p>
              </div>

              <div
                className="p-6 rounded-lg border"
                style={{ borderColor: "var(--light-gray)" }}
              >
                <h3
                  className="font-bold mb-2"
                  style={{ color: "var(--dark-navy)" }}
                >
                  Septic Tank Installation &amp; Replacement
                </h3>
                <p
                  className="mb-3 text-sm"
                  style={{ color: "var(--text-light)" }}
                >
                  Concrete, fiberglass, and poly septic tank installations with proper elevations, bedding,
                  and connections for long-term performance.
                </p>
                <Link
                  to="/services/septic-tank-installation-thunder-bay"
                  className="text-orange-600 text-sm font-semibold hover:underline"
                >
                  View Septic Tank Installation Service
                </Link>
              </div>
            </div>
          </div>

          {/* Pre-Communities CTA */}
          <div
            className="mb-16 text-center p-8 rounded-lg border"
            style={{ borderColor: "var(--primary-orange)" }}
          >
            <h2
              className="text-3xl font-bold mb-3"
              style={{ color: "var(--dark-navy)" }}
            >
              Ready to Get Started?
            </h2>
            <p
              className="text-lg mb-4"
              style={{ color: "var(--text-light)" }}
            >
              Most installations are completed in about 3 days on average with full permit handling and engineered design.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                className="px-6 py-3 rounded-md font-semibold text-white hover:opacity-90"
                style={{ backgroundColor: "var(--primary-orange)" }}
              >
                Request Free Estimate
              </Link>
              <a
                href={`tel:${companyInfo.phone}`}
                className="inline-flex items-center space-x-2 px-6 py-3 rounded-md font-semibold border hover:bg-orange-50"
                style={{ borderColor: "var(--primary-orange)", color: "var(--dark-navy)" }}
              >
                <Phone size={20} />
                <span>Call {companyInfo.phone}</span>
              </a>
            </div>
          </div>

          {/* Thunder Bay Area Communities */}
          <div className="mb-16">
            <h2
              className="text-3xl font-bold mb-8 text-center"
              style={{ color: "var(--dark-navy)" }}
            >
              Thunder Bay Area Communities
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {geoPages
                .filter((page) => page.region === "Thunder Bay Area")
                .map((page) => (
                  <Link
                    key={page.slug}
                    to={`/service-areas/${page.slug}`}
                    className="p-4 rounded-lg border-2 hover:shadow-md text-center"
                    style={{
                      borderColor: "var(--primary-orange)",
                      transition: "box-shadow 0.3s ease"
                    }}
                  >
                    <MapPin
                      size={24}
                      style={{ color: "var(--primary-orange)" }}
                      className="mx-auto mb-2"
                    />
                    <span
                      className="font-medium"
                      style={{ color: "var(--dark-navy)" }}
                    >
                      {page.city}
                    </span>
                  </Link>
                ))}
            </div>
          </div>

          {/* North Shore */}
          <div className="mb-16">
            <h2
              className="text-3xl font-bold mb-8 text-center"
              style={{ color: "var(--dark-navy)" }}
            >
              North Shore Communities
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {geoPages
                .filter((page) => page.region === "North Shore")
                .map((page) => (
                  <Link
                    key={page.slug}
                    to={`/service-areas/${page.slug}`}
                    className="p-4 rounded-lg border hover:shadow-md text-center"
                    style={{
                      borderColor: "var(--light-gray)",
                      transition: "box-shadow 0.3s ease"
                    }}
                  >
                    <MapPin
                      size={24}
                      style={{ color: "var(--primary-orange)" }}
                      className="mx-auto mb-2"
                    />
                    <span
                      className="font-medium"
                      style={{ color: "var(--dark-navy)" }}
                    >
                      {page.city}
                    </span>
                  </Link>
                ))}
            </div>
          </div>

          {/* Northwestern Ontario */}
          <div className="mb-16">
            <h2
              className="text-3xl font-bold mb-8 text-center"
              style={{ color: "var(--dark-navy)" }}
            >
              Northwestern Ontario
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {geoPages
                .filter((page) => page.region === "Northwestern Ontario")
                .map((page) => (
                  <Link
                    key={page.slug}
                    to={`/service-areas/${page.slug}`}
                    className="p-4 rounded-lg border hover:shadow-md text-center"
                    style={{
                      borderColor: "var(--light-gray)",
                      transition: "box-shadow 0.3s ease"
                    }}
                  >
                    <MapPin
                      size={24}
                      style={{ color: "var(--primary-orange)" }}
                      className="mx-auto mb-2"
                    />
                    <span
                      className="font-medium"
                      style={{ color: "var(--dark-navy)" }}
                    >
                      {page.city}
                    </span>
                  </Link>
                ))}
            </div>
          </div>

          {/* CTA Section */}
          <div
            className="text-center p-8 rounded-lg"
            style={{ backgroundColor: "var(--light-gray)" }}
          >
            <h2
              className="text-3xl font-bold mb-4"
              style={{ color: "var(--dark-navy)" }}
            >
              Not Sure If We Serve Your Area?
            </h2>
            <p
              className="text-lg mb-6"
              style={{ color: "var(--text-light)" }}
            >
              Give us a call! We&apos;re expanding throughout Northwestern Ontario and may be able to help.
            </p>
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center space-x-2 px-8 py-4 rounded-md font-bold text-white hover:opacity-90"
              style={{ backgroundColor: "var(--primary-orange)" }}
            >
              <Phone size={24} />
              <span>{companyInfo.phone}</span>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceAreasPage;
